import React from 'react';
import { downloadCSV } from '_utils/csv-export';

const titleHeaderStyle = {
    border: '1px solid #374151',
    padding: '10px 14px',
    textAlign: 'center',
    backgroundColor: '#4b5563',
    color: '#fff',
    fontWeight: 600,
    fontSize: '14px',
};

const sectionHeaderStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    backgroundColor: '#f3f4f6',
    fontWeight: 600,
    fontSize: '13px',
    color: '#374151',
};

const headerStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'center',
    backgroundColor: '#6b7280',
    color: '#fff',
    fontWeight: 600,
    fontSize: '13px',
};

const cellStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#1f2937',
};

const labelCellStyle = {
    ...cellStyle,
    textAlign: 'left',
    fontWeight: 500,
};

const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
};

const EcacDepartureSummaryTable = ({ summary, startDate, endDate }) => {
    if (!summary) {
        return <p style={{ fontSize: '14px', color: '#6b7280' }}>No data available. Run the report to see results.</p>;
    }

    const preAdvised = summary.preAdvised;
    const nonAdvised = summary.nonAdvised;
    const hasData =
        (preAdvised?.tiers?.length || 0) > 0 ||
        (nonAdvised?.tiers?.length || 0) > 0 ||
        (summary.departureServices?.totalDepartureServices ?? 0) > 0;

    if (!hasData) {
        return <p style={{ fontSize: '14px', color: '#6b7280' }}>No departure data for the selected date range.</p>;
    }

    const renderTierRows = (tiers, lastThreshold) => {
        if (!tiers?.length) return null;
        return tiers.map((tier, idx) => {
            const label = tier.mins != null
                ? `${tier.mins} Mins (${tier.targetPct}%)`
                : `> ${lastThreshold ?? 30} Mins (100%)`;
            const count = tier.countOver != null ? tier.countOver : tier.countWithin;
            const pct = tier.countOverPct != null ? tier.countOverPct : tier.countWithinPct;
            const actualDisplay = pct != null ? `${count} (${pct}%)` : String(count);
            return (
                <tr key={idx}>
                    <td style={labelCellStyle}>{label}</td>
                    <td style={cellStyle}>{tier.targetPct}%</td>
                    <td style={cellStyle}>{actualDisplay}</td>
                </tr>
            );
        });
    };

    const getLastThreshold = (tiers) => {
        const withMins = (tiers || []).filter((t) => t.mins != null);
        return withMins.length ? Math.max(...withMins.map((t) => t.mins)) : 30;
    };
    const preLastMins = getLastThreshold(preAdvised?.tiers) ?? 30;
    const nonLastMins = getLastThreshold(nonAdvised?.tiers) ?? 45;

    const handleExportExcel = async () => {
        const Excel = await import('exceljs/dist/exceljs.min.js');
        const workbook = new Excel.Workbook();
        const sheet = workbook.addWorksheet('ECAC Departure Summary');
        const dateLabel = startDate && endDate ? `${startDate} - ${endDate}` : 'Actual';

        sheet.getColumn(1).width = 38;
        sheet.getColumn(2).width = 12;
        sheet.getColumn(3).width = 28;

        const COLORS = {
            title: 'FF4B5563',
            colHeader: 'FF6B7280',
            section: 'FFF3F4F6',
            white: 'FFFFFFFF',
            whiteText: 'FFFFFFFF',
            darkText: 'FF374151',
        };
        const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

        const applyCell = (cell, { fill, font, align = 'center' } = {}) => {
            cell.border = border;
            cell.alignment = { vertical: 'middle', horizontal: align, wrapText: true };
            if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
            if (font) cell.font = font;
        };

        const addTitleRow = (label) => {
            const row = sheet.addRow([label, '', '']);
            sheet.mergeCells(`A${row.number}:C${row.number}`);
            applyCell(row.getCell(1), { fill: COLORS.title, font: { bold: true, color: { argb: COLORS.whiteText }, size: 13 } });
            row.height = 26;
        };

        const addHeaderRow = (col1, col2, col3) => {
            const row = sheet.addRow([col1, col2, col3]);
            applyCell(row.getCell(1), { fill: COLORS.colHeader, font: { bold: true, color: { argb: COLORS.whiteText }, size: 12 }, align: 'left' });
            applyCell(row.getCell(2), { fill: COLORS.colHeader, font: { bold: true, color: { argb: COLORS.whiteText }, size: 12 } });
            applyCell(row.getCell(3), { fill: COLORS.colHeader, font: { bold: true, color: { argb: COLORS.whiteText }, size: 12 } });
            row.height = 24;
        };

        const addSectionRow = (label) => {
            const row = sheet.addRow([label, '', '']);
            sheet.mergeCells(`A${row.number}:C${row.number}`);
            applyCell(row.getCell(1), { fill: COLORS.section, font: { bold: true, color: { argb: COLORS.darkText }, size: 12 }, align: 'left' });
            row.height = 24;
        };

        const addTierRow = (label, targetPct, actual) => {
            const row = sheet.addRow([label, targetPct, actual]);
            applyCell(row.getCell(1), { align: 'left' });
            applyCell(row.getCell(2));
            applyCell(row.getCell(3));
            row.height = 22;
        };

        const addServiceRow = (label, value) => {
            const row = sheet.addRow([label, value, '']);
            sheet.mergeCells(`B${row.number}:C${row.number}`);
            applyCell(row.getCell(1), { align: 'left' });
            applyCell(row.getCell(2));
            row.height = 22;
        };

        addTitleRow('Departures');
        addHeaderRow('Standard', 'Target %', dateLabel);

        addSectionRow(`Number Of Preadvised PRMs${preAdvised?.total != null ? ` (Total: ${preAdvised.total})` : ''}`);
        (preAdvised?.tiers || []).forEach((tier) => {
            const label = tier.mins != null ? `${tier.mins} Mins` : `> ${preLastMins} Mins`;
            const count = tier.countOver != null ? tier.countOver : tier.countWithin;
            const pct = tier.countOverPct != null ? tier.countOverPct : tier.countWithinPct;
            addTierRow(label, `${tier.targetPct}%`, pct != null ? `${count} (${pct}%)` : String(count));
        });

        addSectionRow(`Number Of Non Advised PRMs${nonAdvised?.total != null ? ` (Total: ${nonAdvised.total})` : ''}`);
        (nonAdvised?.tiers || []).forEach((tier) => {
            const label = tier.mins != null ? `${tier.mins} Mins` : `> ${nonLastMins} Mins`;
            const count = tier.countOver != null ? tier.countOver : tier.countWithin;
            const pct = tier.countOverPct != null ? tier.countOverPct : tier.countWithinPct;
            addTierRow(label, `${tier.targetPct}%`, pct != null ? `${count} (${pct}%)` : String(count));
        });

        if (summary.departureServices) {
            const ds = summary.departureServices;
            addSectionRow('Departure Services');
            addServiceRow('Departure Services', ds.departureServices ?? 0);
            addServiceRow('Disregard - No Shows', ds.disregardNoShows ?? 0);
            addServiceRow('Pre-Advised', ds.preAdvised ?? 0);
            addServiceRow('Non-Advised', ds.nonAdvised ?? 0);
            addServiceRow('Pre-Advised (Depart Pax No Show)', ds.preAdvisedDepartPaxNoShow ?? 0);
            addServiceRow('Non-Advised (Depart Pax No Show)', ds.nonAdvisedDepartPaxNoShow ?? 0);
            addServiceRow('Total Departure Services', ds.totalDepartureServices ?? 0);
            addServiceRow('Missing Data', ds.missingData ?? 0);
            addServiceRow('Missing Data Percent %', ds.missingDataPct != null ? `${ds.missingDataPct}%` : '');
        }

        const buf = await workbook.xlsx.writeBuffer();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
        link.download = `ecac-departure-summary-${new Date().toISOString().slice(0, 10)}.xlsx`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const handleExportCsv = () => {
        const dateLabel = startDate && endDate ? `${startDate} - ${endDate}` : 'Actual';
        const rows = [];

        rows.push({ Standard: 'Number Of Preadvised PRMs', 'Target %': preAdvised?.total != null ? `Total: ${preAdvised.total}` : '', [dateLabel]: '' });
        (preAdvised?.tiers || []).forEach((tier) => {
            const label = tier.mins != null ? `${tier.mins} Mins` : `> ${preLastMins} Mins`;
            const count = tier.countOver != null ? tier.countOver : tier.countWithin;
            const pct = tier.countOverPct != null ? tier.countOverPct : tier.countWithinPct;
            rows.push({ Standard: label, 'Target %': `${tier.targetPct}%`, [dateLabel]: pct != null ? `${count} (${pct}%)` : String(count) });
        });

        rows.push({ Standard: 'Number Of Non Advised PRMs', 'Target %': nonAdvised?.total != null ? `Total: ${nonAdvised.total}` : '', [dateLabel]: '' });
        (nonAdvised?.tiers || []).forEach((tier) => {
            const label = tier.mins != null ? `${tier.mins} Mins` : `> ${nonLastMins} Mins`;
            const count = tier.countOver != null ? tier.countOver : tier.countWithin;
            const pct = tier.countOverPct != null ? tier.countOverPct : tier.countWithinPct;
            rows.push({ Standard: label, 'Target %': `${tier.targetPct}%`, [dateLabel]: pct != null ? `${count} (${pct}%)` : String(count) });
        });

        if (summary.departureServices) {
            const ds = summary.departureServices;
            rows.push({ Standard: 'Departure Services', 'Target %': '', [dateLabel]: '' });
            rows.push({ Standard: 'Departure Services', 'Target %': '', [dateLabel]: ds.departureServices ?? 0 });
            rows.push({ Standard: 'Disregard - No Shows', 'Target %': '', [dateLabel]: ds.disregardNoShows ?? 0 });
            rows.push({ Standard: 'Pre-Advised', 'Target %': '', [dateLabel]: ds.preAdvised ?? 0 });
            rows.push({ Standard: 'Non-Advised', 'Target %': '', [dateLabel]: ds.nonAdvised ?? 0 });
            rows.push({ Standard: 'Pre-Advised (Depart Pax No Show)', 'Target %': '', [dateLabel]: ds.preAdvisedDepartPaxNoShow ?? 0 });
            rows.push({ Standard: 'Non-Advised (Depart Pax No Show)', 'Target %': '', [dateLabel]: ds.nonAdvisedDepartPaxNoShow ?? 0 });
            rows.push({ Standard: 'Total Departure Services', 'Target %': '', [dateLabel]: ds.totalDepartureServices ?? 0 });
            rows.push({ Standard: 'Missing Data', 'Target %': '', [dateLabel]: ds.missingData ?? 0 });
            rows.push({ Standard: 'Missing Data Percent %', 'Target %': '', [dateLabel]: ds.missingDataPct != null ? `${ds.missingDataPct}%` : '' });
        }

        downloadCSV(rows, `ecac-departure-summary-${new Date().toISOString().slice(0, 10)}.csv`);
    };

    return (
        <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '8px' }}>
                <button
                    type="button"
                    onClick={handleExportCsv}
                    style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                    }}
                >
                    Export CSV
                </button>
                <button
                    type="button"
                    onClick={handleExportExcel}
                    style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        backgroundColor: '#dcfce7',
                        color: '#166534',
                        border: '1px solid #bbf7d0',
                        borderRadius: '6px',
                    }}
                >
                    Export Excel
                </button>
            </div>
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={titleHeaderStyle} colSpan={3}>
                            Departures
                        </th>
                    </tr>
                    <tr>
                        <th style={{ ...headerStyle, textAlign: 'left' }}>Standard</th>
                        <th style={headerStyle}>Target %</th>
                        <th style={headerStyle}>{startDate && endDate ? `${startDate} - ${endDate}` : 'Actual'}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={sectionHeaderStyle} colSpan={3}>
                            Number Of Preadvised PRMs {preAdvised?.total != null ? `(Total: ${preAdvised.total})` : ''}
                        </td>
                    </tr>
                    {renderTierRows(preAdvised?.tiers, preLastMins)}
                    <tr>
                        <td style={sectionHeaderStyle} colSpan={3}>
                            Number Of Non Advised PRMs {nonAdvised?.total != null ? `(Total: ${nonAdvised.total})` : ''}
                        </td>
                    </tr>
                    {renderTierRows(nonAdvised?.tiers, nonLastMins)}
                    {summary.departureServices && (
                        <>
                            <tr>
                                <td style={sectionHeaderStyle} colSpan={3}>
                                    Departure Services
                                </td>
                            </tr>
                            <tr>
                                <td style={labelCellStyle}>Departure Services</td>
                                <td style={cellStyle} colSpan={2}>{summary.departureServices.departureServices ?? 0}</td>
                            </tr>
                            <tr>
                                <td style={labelCellStyle}>Disregard - No Shows</td>
                                <td style={cellStyle} colSpan={2}>{summary.departureServices.disregardNoShows ?? 0}</td>
                            </tr>
                            <tr>
                                <td style={labelCellStyle}>Pre-Advised</td>
                                <td style={cellStyle} colSpan={2}>{summary.departureServices.preAdvised ?? 0}</td>
                            </tr>
                            <tr>
                                <td style={labelCellStyle}>Non-Advised</td>
                                <td style={cellStyle} colSpan={2}>{summary.departureServices.nonAdvised ?? 0}</td>
                            </tr>
                            <tr>
                                <td style={labelCellStyle}>Pre-Advised (Depart Pax No Show)</td>
                                <td style={cellStyle} colSpan={2}>{summary.departureServices.preAdvisedDepartPaxNoShow ?? 0}</td>
                            </tr>
                            <tr>
                                <td style={labelCellStyle}>Non-Advised (Depart Pax No Show)</td>
                                <td style={cellStyle} colSpan={2}>{summary.departureServices.nonAdvisedDepartPaxNoShow ?? 0}</td>
                            </tr>
                            <tr>
                                <td style={labelCellStyle}>Total Departure Services</td>
                                <td style={cellStyle} colSpan={2}>{summary.departureServices.totalDepartureServices ?? 0}</td>
                            </tr>
                            <tr>
                                <td style={labelCellStyle}>Missing Data</td>
                                <td style={cellStyle} colSpan={2}>{summary.departureServices.missingData ?? 0}</td>
                            </tr>
                            <tr>
                                <td style={labelCellStyle}>Missing Data Percent %</td>
                                <td style={cellStyle} colSpan={2}>
                                    {summary.departureServices.missingDataPct != null
                                        ? `${summary.departureServices.missingDataPct}%`
                                        : ''}
                                </td>
                            </tr>
                        </>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default EcacDepartureSummaryTable;
